import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Star, Clock, Truck, Award, Gem, BadgeCheck, Heart, Users, Timer, TrendingUp, Globe, MapPin, Check, ChevronDown, ChevronUp, Sparkles, Zap } from 'lucide-react';
import { faker } from '@faker-js/faker';
import { formatCurrency } from '../utils/units';

const marketingPoints = [
  {
    icon: Shield,
    text: '10-Year Premium Warranty',
    highlight: '100% Coverage',
    color: 'from-emerald-500/20 to-emerald-600/20',
    stat: '10,000+ Projects'
  },
  {
    icon: Star,
    text: '99% Client Satisfaction',
    highlight: '2,000+ Reviews',
    color: 'from-amber-500/20 to-amber-600/20',
    stat: '4.9/5 Rating'
  },
  {
    icon: Truck,
    text: 'Free Premium Delivery',
    highlight: 'Orders Over €2,000',
    color: 'from-purple-500/20 to-purple-600/20',
    stat: '45+ Countries'
  }
];

const generateActivityData = () => {
  const actions = [
    { type: 'viewed', message: 'browsing designs' },
    { type: 'customized', message: 'customizing' }
  ];
  const products = [
    'Dining Table',
    'Kitchen Countertop',
    'Bathroom Vanity',
    'Coffee Table'
  ];
  
  const action = faker.helpers.arrayElement(actions);
  const product = faker.helpers.arrayElement(products);

  return {
    location: faker.location.city() + ', ' + faker.location.country(),
    action: action.type,
    message: action.message,
    product: product,
    time: 'Just now',
    userCount: faker.number.int({ min: 1, max: 5 }),
    activeUsers: faker.number.int({ min: 50, max: 200 }),
    projectsInProgress: faker.number.int({ min: 5, max: 20 }),
    completionRate: faker.number.int({ min: 95, max: 100 })
  };
};

export const MarketingHeader: React.FC = () => {
  const [isAutoScrollPaused, setIsAutoScrollPaused] = useState(false);
  const [recentActivity, setRecentActivity] = useState(generateActivityData());
  const [showActivity, setShowActivity] = useState(true);
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);

  useEffect(() => {
    const activityTimer = setInterval(() => {
      setShowActivity(false);
      setTimeout(() => {
        setRecentActivity(generateActivityData());
        setShowActivity(true);
      }, 500);
    }, 5000);

    return () => clearInterval(activityTimer);
  }, []);

  return (
    <div className="marketing-header">
      {/* Desktop View */}
      <div className="hidden lg:block h-full">
        <div className="marketing-header-inner">
          {/* Marketing Points */}
          <div className="marketing-points">
            {marketingPoints.map((point, index) => {
              const Icon = point.icon;
              return (
                <motion.div
                  key={`${point.text}-${index}`}
                  className="marketing-point"
                  onMouseEnter={() => setIsAutoScrollPaused(true)}
                  onMouseLeave={() => setIsAutoScrollPaused(false)}
                >
                  <div className={`marketing-point-icon bg-gradient-to-br ${point.color}`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="marketing-point-text">
                    <p className="text-white text-sm font-medium truncate min-w-[200px]">
                      {point.text}
                    </p>
                    <div className="flex items-center justify-between">
                      <p className="text-gold text-xs truncate min-w-[140px]">
                        {point.highlight}
                      </p>
                      <span className="text-white/60 text-xs ml-6">
                        {point.stat}
                      </span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Live Activity Feed */}
          <AnimatePresence mode="wait">
            {showActivity && (
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                className="marketing-activity min-w-[480px]"
              >
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <div className="flex items-center gap-1 text-white/80">
                      <MapPin className="w-4 h-4 text-gold" />
                      <span className="truncate max-w-[220px]">{recentActivity.location}</span>
                    </div>
                  </div>
                  <div className="text-sm whitespace-nowrap">
                    <span className="text-white/60">{recentActivity.userCount} users {recentActivity.message}</span>
                    <span className="text-gold mx-1">{recentActivity.product}</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Live Stats */}
          <div className="flex items-center gap-8 ml-8">
            <div className="flex items-center gap-3 min-w-[160px]">
              <Users className="w-4 h-4 text-gold" />
              <span className="text-white/80 text-sm whitespace-nowrap">{recentActivity.activeUsers} active</span>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile View */}
      <div className="lg:hidden">
        <motion.div
          initial={false}
          animate={{ height: isMobileExpanded ? 'auto' : '3.5rem' }}
          className="overflow-hidden"
        >
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-white/90 text-sm font-medium">Live Activity</span>
              </div>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsMobileExpanded(!isMobileExpanded)}
                className="p-2 text-white/60 hover:text-white"
              >
                {isMobileExpanded ? (
                  <ChevronDown className="w-5 h-5" />
                ) : (
                  <ChevronUp className="w-5 h-5" />
                )}
              </motion.button>
            </div>

            {/* Live Stats Cards */}
            <div className="space-y-3">
              {/* Active Users Card */}
              <div className="bg-white/5 rounded-xl p-4 border border-gold/20">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-gold" />
                    <span className="text-white/90 text-sm font-medium">Active Users</span>
                  </div>
                  <span className="text-gold text-lg font-semibold">{recentActivity.activeUsers}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-white/40" />
                  <span className="text-white/60 text-sm">From {faker.number.int({ min: 10, max: 30 })} countries</span>
                </div>
              </div>

              {/* Projects Card */}
              <div className="bg-white/5 rounded-xl p-4 border border-gold/20">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-gold" />
                    <span className="text-white/90 text-sm font-medium">Projects in Progress</span>
                  </div>
                  <span className="text-gold text-lg font-semibold">{recentActivity.projectsInProgress}</span>
                </div>
                <div className="flex items-center gap-2">
                  <BadgeCheck className="w-4 h-4 text-white/40" />
                  <span className="text-white/60 text-sm">{recentActivity.completionRate}% completion rate</span>
                </div>
              </div>

              {/* Latest Activity Card */}
              {showActivity && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-white/5 rounded-xl p-4 border border-gold/20"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Timer className="w-5 h-5 text-gold" />
                      <span className="text-white/90 text-sm font-medium">Latest Activity</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-white/40" />
                      <span className="text-white/90 text-sm truncate">{recentActivity.location}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-white/60 text-sm">{recentActivity.userCount} users {recentActivity.message}</span>
                      <span className="text-gold text-sm">{recentActivity.product}</span>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};